// src/components/Invitation/CollaboratorSignup.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import './invitation.css';

function CollaboratorSignup({ invitationData, onSignupSuccess, onCancel }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Basic validation
    if (!name || !email || !password) {
      setError('All fields are required');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update the user's display name
      await updateProfile(user, { displayName: name });
      
      // Create the workspace entry
      const workspaceEntry = {
        ownerUid: invitationData.ownerUid,
        profileId: invitationData.profileId,
        workspaceName: invitationData.profileName || "Workspace",
        invitedAt: new Date(),
        role: 'collaborator',
        acceptedAt: new Date()
      };
      
      // Create a user document in Firestore with the new structure
      await setDoc(doc(db, 'users', user.uid), {
        email: email,
        name: name,
        displayName: name,
        isCollaborator: true,
        collaboratorSince: serverTimestamp(),
        invitedBy: invitationData.ownerUid, // Keep for backward compatibility
        originalInvitation: {  // Keep for backward compatibility
          token: invitationData.token,
          profileId: invitationData.profileId,
          workspaceName: invitationData.profileName
        },
        // New field for multiple workspaces
        collaboratorWorkspaces: [workspaceEntry],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log("Collaborator account created successfully!");
      
      if (onSignupSuccess) {
        onSignupSuccess(user);
      }
    } catch (error) {
      console.error("Error creating collaborator account:", error);
      
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please log in instead.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        setError('Please choose a stronger password.');
      } else {
        setError('Failed to create account: ' + error.message);
      }
      
      setIsLoading(false);
    }
  };

  return (
    <div className="invitation-card">
      <h2 className="invitation-title">Create Collaborator Account</h2>
      
      <form onSubmit={handleSubmit} className="invitation-form">
        <div className="invitation-form-group">
          <label htmlFor="name" className="invitation-form-label">
            Full Name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="invitation-form-input"
            placeholder="John Doe"
            required
          />
        </div>
        
        <div className="invitation-form-group">
          <label htmlFor="email" className="invitation-form-label">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="invitation-form-input"
            placeholder="john@example.com"
            required
          />
        </div>
        
        <div className="invitation-form-group">
          <label htmlFor="password" className="invitation-form-label">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="invitation-form-input"
            placeholder="••••••••"
            required
            minLength={6}
          />
        </div>
        
        <div className="invitation-form-group">
          <label htmlFor="confirmPassword" className="invitation-form-label">
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="invitation-form-input"
            placeholder="••••••••"
            required
          />
        </div>
        
        {error && (
          <div className="invitation-error">
            {error}
          </div>
        )}
        
        <p className="invitation-note">
          By signing up, you are creating a free collaborator account to access the invited workspace.
          This account does not include a subscription plan.
        </p>
        
        <div className="invitation-button-group">
          <button
            type="button"
            onClick={onCancel}
            className="invitation-secondary-button"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="invitation-action-button"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CollaboratorSignup;